import {
  Box, CircularProgress, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TargetsApi from 'api/executive/targets.api'
import { useBranches } from 'features/executive/shared/hooks/use-reference-data'
import type { RevenueTarget } from 'api/executive/executive.types'
import { formatCurrency, MONTH_SHORT_TH, toBuddhistYear, toGregorianYear } from 'utils/executive.utils'
import ButtonCustom from 'component/Button/ButtonCustom'
import InputTextField from 'component/Input/InputTextField'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import InfoBanner from 'new-components/banners/info-banner'
import PageModal from 'new-components/modal/page-modal'
import TabLoading from './TabLoading'

type Cell = { branchId: number; month: number; amount: number | null; targetId?: number }

export default function RevenueTargetsTab() {
  const { t } = useTranslation()
  const [beYear, setBeYear] = useState(() => toBuddhistYear(new Date().getFullYear()))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [cells, setCells] = useState<Cell[]>([])
  const [baseline, setBaseline] = useState<Map<string, number | null>>(new Map())
  /** Open state for the "copy will overwrite unsaved edits" confirm dialog. */
  const [confirmCopy, setConfirmCopy] = useState(false)

  const { data: branches, loading: branchesLoading } = useBranches({ scope: 'all-branches' })
  const years = [beYear - 1, beYear, beYear + 1]

  const load = async () => {
    if (branches.length === 0) return
    setLoading(true)
    try {
      const res = await TargetsApi.getRevenueTargets({ year: toGregorianYear(beYear) })
      const byKey = new Map<string, RevenueTarget>()
      res.items.forEach((it) => {
        if (it.branchId != null && it.month != null) {
          byKey.set(`${it.branchId}-${it.month}`, it)
        }
      })
      const next: Cell[] = []
      for (const b of branches) {
        for (let m = 1; m <= 12; m++) {
          const existing = byKey.get(`${b.id}-${m}`)
          next.push({
            branchId: b.id,
            month: m,
            amount: existing?.amount ?? null,
            targetId: existing?.id,
          })
        }
      }
      setCells(next)
      setBaseline(new Map(next.map((c) => [`${c.branchId}-${c.month}`, c.amount])))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [beYear, branches])

  const updateCell = (branchId: number, month: number, amount: number | null) => {
    setCells((prev) => prev.map((c) =>
      c.branchId === branchId && c.month === month ? { ...c, amount } : c
    ))
  }

  /** Pull last year's amounts into the grid for the SAME set of currently-active
   *  branches. Branches that existed last year but are now inactive are dropped;
   *  branches active now that didn't exist last year stay empty. The current
   *  year's `targetId` per cell is preserved so Save still updates in place
   *  instead of creating duplicate rows. */
  const doCopy = async () => {
    const srcBeYear = beYear - 1
    setLoading(true)
    try {
      const res = await TargetsApi.getRevenueTargets({ year: toGregorianYear(srcBeYear) })
      if (res.items.length === 0) {
        notiError(t('DASHBOARD_TARGETS.REVENUE.COPY_NO_DATA', {
          defaultValue: `ไม่มีข้อมูลเป้ารายได้ปี ${srcBeYear} ให้คัดลอก`,
          year: srcBeYear,
        }) as string)
        return
      }
      const srcByKey = new Map<string, RevenueTarget>()
      res.items.forEach((it) => {
        if (it.branchId != null && it.month != null) {
          srcByKey.set(`${it.branchId}-${it.month}`, it)
        }
      })
      // Preserve THIS year's targetIds so Save updates in place; only the
      // amount field comes from the source year.
      const currentIdByKey = new Map<string, number | undefined>()
      cells.forEach((c) => currentIdByKey.set(`${c.branchId}-${c.month}`, c.targetId))

      const next: Cell[] = []
      for (const b of branches) {
        for (let m = 1; m <= 12; m++) {
          const key = `${b.id}-${m}`
          const src = srcByKey.get(key)
          next.push({
            branchId: b.id,
            month: m,
            amount: src?.amount ?? null,
            targetId: currentIdByKey.get(key),
          })
        }
      }
      setCells(next)
      notiSuccess(t('DASHBOARD_TARGETS.REVENUE.COPY_SUCCESS', {
        defaultValue: `คัดลอกจากปี ${srcBeYear} แล้ว — กดบันทึกเพื่อยืนยัน`,
        year: srcBeYear,
      }) as string)
    } catch (e) {
      notiError(t('DASHBOARD_TARGETS.REVENUE.COPY_ERROR', { defaultValue: 'คัดลอกข้อมูลล้มเหลว' }) as string)
    } finally {
      setLoading(false)
    }
  }

  /** Toolbar button handler — if there are unsaved edits, open the confirm
   *  dialog first; otherwise copy immediately. */
  const handleCopyClick = () => {
    if (hasUnsavedChanges) setConfirmCopy(true)
    else doCopy()
  }

  const save = async () => {
    setSaving(true)
    try {
      const payload = cells.map((c) => ({
        id: c.targetId,
        branchId: c.branchId,
        year: toGregorianYear(beYear),
        month: c.month,
        day: null,
        amount: c.amount ?? 0,
      }))
      const filtered = payload.filter((p) => p.amount !== 0 || p.id)
      await TargetsApi.upsertRevenueTargets({ items: filtered as any })
      notiSuccess(t('DASHBOARD_TARGETS.SAVE_SUCCESS') as string)
      await load()
    } catch (e) {
      notiError(t('DASHBOARD_TARGETS.SAVE_ERROR') as string)
    } finally {
      setSaving(false)
    }
  }

  const yearTotals = useMemo(() => {
    const byBranch = new Map<number, number>()
    cells.forEach((c) => {
      if (c.amount != null) byBranch.set(c.branchId, (byBranch.get(c.branchId) ?? 0) + c.amount)
    })
    return byBranch
  }, [cells])

  const monthTotals = useMemo(() => {
    const byMonth = new Map<number, number>()
    cells.forEach((c) => {
      if (c.amount != null) byMonth.set(c.month, (byMonth.get(c.month) ?? 0) + c.amount)
    })
    return byMonth
  }, [cells])

  const cellMap = useMemo(() => {
    const m = new Map<string, Cell>()
    cells.forEach((c) => m.set(`${c.branchId}-${c.month}`, c))
    return m
  }, [cells])

  const hasUnsavedChanges = useMemo(() => {
    for (const c of cells) {
      if (baseline.get(`${c.branchId}-${c.month}`) !== c.amount) return true
    }
    return false
  }, [cells, baseline])

  return (
    <Box>
      <InfoBanner sx={{ mb: 2 }}>
        {t('DASHBOARD_TARGETS.REVENUE_HINT', {
          defaultValue:
            'ตั้งเป้ารายได้รายเดือนต่อสาขา — ค่าจะไปอยู่ใน KPI ของหน้าแดชบอร์ดและรายงานผู้บริหาร',
        })}
      </InfoBanner>

      {/*
        Every save commits exactly one year — the one shown in the toolbar
        at the click moment. The year selector and Save button are locked
        whenever `saving || loading` is true so the toolbar context cannot
        race an in-flight load/save. Mirrors ProcedureTargetsTab.
      */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="body2">{t('DASHBOARD_TARGETS.YEAR')}</Typography>
        <Select
          size="small"
          value={beYear}
          onChange={(e) => setBeYear(Number(e.target.value))}
          disabled={saving || loading}
        >
          {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </Select>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          <ButtonCustom
            variant="outlined"
            onClick={handleCopyClick}
            disabled={saving || loading}
            textButton={t('DASHBOARD_TARGETS.REVENUE.COPY_LAST_YEAR', {
              defaultValue: `คัดลอกจากปี ${beYear - 1}`,
              year: beYear - 1,
            })}
          />
          <ButtonCustom
            variant="contained"
            onClick={save}
            disabled={saving || loading || !hasUnsavedChanges}
            textButton={saving ? <CircularProgress size={20} /> : t('DASHBOARD_TARGETS.SAVE')}
          />
        </Box>
      </Box>

      {loading || branchesLoading ? (
        <TabLoading />
      ) : (
      <Paper sx={{ overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ position: 'sticky', left: 0, zIndex: 3 }}>
                  {t('EXECUTIVE.FILTER.BRANCH')}
                </TableCell>
                {MONTH_SHORT_TH.map((m) => (
                  <TableCell key={m} align="right">{m}</TableCell>
                ))}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{t('DASHBOARD_TARGETS.TOTAL')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {branches.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell sx={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'background.paper' }}>
                    {b.name}
                  </TableCell>
                  {Array.from({ length: 12 }).map((_, i) => {
                    const m = i + 1
                    const cell = cellMap.get(`${b.id}-${m}`)
                    return (
                      <TableCell key={m} align="right">
                        <InputTextField
                          hookValue
                          inputHeight={32}
                          type="number"
                          inputTextAlign="right"
                          placeholder="0"
                          value={cell?.amount ?? ''}
                          onchange={(e: any) =>
                            updateCell(b.id, m, e.target.value === '' ? null : Number(e.target.value))
                          }
                          inputProps={{ min: 0 }}
                          style={{ width: 140 }}
                        />
                      </TableCell>
                    )
                  })}
                  <TableCell align="right">{formatCurrency(yearTotals.get(b.id) ?? 0)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 600, position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'background.paper' }}>
                  {t('DASHBOARD_TARGETS.ALL_BRANCHES')}
                </TableCell>
                {Array.from({ length: 12 }).map((_, i) => (
                  <TableCell key={i} align="right" sx={{ fontWeight: 600 }}>
                    {formatCurrency(monthTotals.get(i + 1) ?? 0)}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {formatCurrency(Array.from(yearTotals.values()).reduce((a, b) => a + b, 0))}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {t('DASHBOARD_TARGETS.BRANCH_MONTH_COUNT', {
            defaultValue: '{{branches}} สาขา × 12 เดือน',
            branches: branches.length,
          })}
          {hasUnsavedChanges &&
            ` · ${t('DASHBOARD_TARGETS.UNSAVED', 'มีการเปลี่ยนแปลงที่ยังไม่บันทึก')}`}
        </Typography>
      </Box>

      <PageModal
        isOpen={confirmCopy}
        size="XS"
        title={t('DASHBOARD_TARGETS.REVENUE.COPY_TITLE', { defaultValue: 'ยืนยันคัดลอกจากปีก่อน' }) as string}
        handleClose={() => setConfirmCopy(false)}
        onCancel={{
          onClick: () => setConfirmCopy(false),
          title: t('DASHBOARD_TARGETS.PICKER.CANCEL', { defaultValue: 'ยกเลิก' }) as string,
        }}
        onSubmit={{
          onClick: () => {
            setConfirmCopy(false)
            doCopy()
          },
          title: t('DASHBOARD_TARGETS.REVENUE.COPY_CONFIRM', { defaultValue: 'คัดลอกทับ' }) as string,
          type: 'danger',
        }}
      >
        <Typography variant="body2">
          {t('DASHBOARD_TARGETS.REVENUE.COPY_BODY', {
            defaultValue: `การเปลี่ยนแปลงที่ยังไม่บันทึกในปี ${beYear} จะถูกแทนที่ด้วยค่าจากปี ${beYear - 1}`,
            year: beYear,
            srcYear: beYear - 1,
          })}
        </Typography>
      </PageModal>
    </Box>
  )
}
